import { ApiAIBillingType, ApiBilling } from "@incendium/api";
import { Edit } from "@mui/icons-material";
import {
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import Loading from "Components/Loading/Loading";
import SpacedList from "Components/UI/SpacedList";
import { cell1Icon } from "consts";
import { camelToText } from "Helpers/camelToText";
import { useNotification } from "Hooks";
import produce from "immer";
import { useCallback, useMemo, useState } from "react";
import { createBilling } from "features/ai/services/aiService";
import useAIBillings from "features/ai/hooks/useAIBillings";
import { sortAlpha } from "Helpers/arrays";
import AICreditsDrawer from "features/ai/components/AICreditsDrawer";

function formatBillingType(t: ApiAIBillingType | string): string {
  return camelToText(String(t).replace(/^AIBillingType/, ""));
}

function AICreditsPage() {
  const { billings, setBillings, loading } = useAIBillings();
  const [open, setOpen] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState<ApiBilling | null>(null);
  const [fetchLoading, setFetchLoading] = useState<ApiAIBillingType | null>(null);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const sorted = useMemo(
    () => sortAlpha([...(billings ?? [])], "type"),
    [billings]
  );

  const onEdit = useCallback((b: ApiBilling) => {
    setSelectedBilling(b);
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setSelectedBilling(null);
  }, []);

  const onSubmit = useCallback(
    async (b: ApiBilling) => {
      if (!b.type) return;
      setFetchLoading(b.type);
      try {
        const res = await createBilling({
          type: b.type,
          name: b.name?.trim() ?? "",
          cost: b.cost ?? 0,
        });
        setBillings((prev) =>
          produce(prev, (draft) => {
            const idx = draft.findIndex((d) => d.type === res.type);
            if (idx >= 0) {
              draft[idx] = res;
            } else {
              draft.push(res);
            }
          })
        );
        showSuccessNotification("AI billing item saved");
        onClose();
      } catch {
        showErrorNotification("Failed to save AI billing item");
      } finally {
        setFetchLoading(null);
      }
    },
    [
      setBillings,
      showSuccessNotification,
      showErrorNotification,
      onClose,
    ]
  );

  if (loading) {
    return <Loading fullHeight />;
  }

  return (
    <>
      <SpacedList title="AI Billing (Credits per use)">
        <TableHead>
          <TableRow>
            <TableCell>Billing Type</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Cost (credits)</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((b) => (
            <TableRow key={b.type}>
              <TableCell>{formatBillingType(b.type ?? "")}</TableCell>
              <TableCell>{b.name ?? "—"}{ b.name === b.type?.toString() ? " (Not Set)" : ""}</TableCell>
              <TableCell>{b.cost != null ? b.cost : "—"}</TableCell>
              <TableCell align="right" style={{ width: cell1Icon }}>
                <IconButton
                  onClick={() => onEdit(b)}
                  size="small"
                  color="primary"
                  disabled={fetchLoading === b.type}
                >
                  {fetchLoading === b.type ? (
                    <Loading size={24} />
                  ) : (
                    <Edit />
                  )}
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SpacedList>
      <AICreditsDrawer
        open={open}
        onClose={onClose}
        billing={selectedBilling}
        onSubmit={onSubmit}
      />
    </>
  );
}

export default AICreditsPage;
